import { useCallback, useState } from 'react';
import { useFetchStreamMetadataMutation } from 'services/api/creativeServer';
import { StreamMetadataDetails } from '../types';

interface FetchArgs {
  url: string;
  role: string;
  adType: string;
  destinationType?: string;
  tvServerEnvironment?: string;
}

interface UseStreamMetadataReturn {
  error: string;
  isLoading: boolean;
  metadata: StreamMetadataDetails | null;
  reset: () => void;
  setDuration: (value: string) => void;
  setStreamState: (
    url: string,
    metadata: StreamMetadataDetails | null,
    error?: string,
  ) => void;
  setUrl: (value: string) => void;
  streamUrl: string;
  validateStream: (args: FetchArgs) => Promise<StreamMetadataDetails | null>;
}

const normalizeError = (error: any) =>
  error?.data?.message ||
  error?.data?.error ||
  error?.error ||
  error?.message ||
  'Failed to fetch stream metadata.';

const normalizeMetadata = (response: any): StreamMetadataDetails | null =>
  response?.data || response || null;

export const useStreamMetadata = (): UseStreamMetadataReturn => {
  const [fetchStreamMetadata, { isLoading }] = useFetchStreamMetadataMutation();
  const [streamUrl, setStreamUrlState] = useState('');
  const [metadata, setMetadata] = useState<StreamMetadataDetails | null>(null);
  const [error, setError] = useState('');

  const reset = useCallback(() => {
    setStreamUrlState('');
    setMetadata(null);
    setError('');
  }, []);

  const setUrl = useCallback((value: string) => {
    setStreamUrlState(value);
    setMetadata(null);
    setError('');
  }, []);

  const setDuration = useCallback((value: string) => {
    setMetadata(current =>
      current
        ? {
            ...current,
            defaultDuration: value,
          }
        : current,
    );
  }, []);

  const setStreamState = useCallback(
    (
      nextUrl: string,
      nextMetadata: StreamMetadataDetails | null,
      nextError = '',
    ) => {
      setStreamUrlState(nextUrl);
      setMetadata(nextMetadata);
      setError(nextError);
    },
    [],
  );

  const validateStream = useCallback(
    async ({
      url,
      role,
      adType,
      destinationType,
      tvServerEnvironment,
    }: FetchArgs) => {
      const trimmedUrl = url.trim();
      setStreamUrlState(trimmedUrl);

      if (!trimmedUrl) {
        setMetadata(null);
        setError('');
        return null;
      }

      try {
        const response = await fetchStreamMetadata({
          url: trimmedUrl,
          role,
          adType,
          ...(destinationType ? { destinationType } : {}),
          ...(tvServerEnvironment ? { tvServerEnvironment } : {}),
        }).unwrap();
        const nextMetadata = normalizeMetadata(response);
        setMetadata(nextMetadata);
        setError('');
        return nextMetadata;
      } catch (err: any) {
        setMetadata(null);
        setError(normalizeError(err));
        return null;
      }
    },
    [fetchStreamMetadata],
  );

  return {
    error,
    isLoading,
    metadata,
    reset,
    setDuration,
    setStreamState,
    setUrl,
    streamUrl,
    validateStream,
  };
};
